/**
 * 通知与通知模板 API
 */
import { buildUrl, request } from './request'

export const MEDICAL_NOTICE_DEPARTMENT = '校医室'

export const NOTICE_PRIORITY_OPTIONS = [
  { label: '紧急', value: '紧急' },
  { label: '重要', value: '重要' },
  { label: '普通', value: '普通' }
]

export const NOTICE_SCOPE_OPTIONS = [
  { label: '全校', value: '全校' },
  { label: '年级', value: '年级' },
  { label: '班级', value: '班级' }
]

function toSafeNumber(value, fallback = 0) {
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : fallback
}

function normalizeText(value, fallback = '') {
  const text = String(value ?? '').trim()
  return text || fallback
}

export function normalizeNoticePriority(value) {
  if (value === '紧急' || value === 0 || value === '0') return '紧急'
  if (value === '重要' || value === 1 || value === '1') return '重要'
  if (value === '普通' || value === 2 || value === '2') return '普通'
  return normalizeText(value, '重要')
}

export function getNoticePriorityCode(value) {
  const normalized = normalizeNoticePriority(value)
  if (normalized === '紧急') return 0
  if (normalized === '重要') return 1
  return 2
}

export function normalizeNoticeScope(value) {
  return normalizeText(value, '全校')
}

export function normalizeNoticeAttachments(value) {
  if (value == null || value === '') return []

  if (Array.isArray(value)) {
    return value.filter(item => item != null && item !== '')
  }

  if (typeof value === 'string') {
    const text = value.trim()
    if (!text) return []
    try {
      const parsed = JSON.parse(text)
      if (Array.isArray(parsed)) return parsed.filter(item => item != null && item !== '')
      return parsed ? [parsed] : []
    } catch {
      return [text]
    }
  }

  if (typeof value === 'object') return [value]
  return []
}

export function formatNoticeTime(isoStr) {
  if (!isoStr) return ''
  try {
    const date = new Date(isoStr)
    if (Number.isNaN(date.getTime())) return String(isoStr)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return String(isoStr)
  }
}

export function normalizeNoticeItem(raw) {
  if (!raw) return null

  const id = raw.notice_id ?? raw.id
  const createTime = raw.create_time ?? raw.createTime ?? ''
  const updateTime = raw.update_time ?? raw.updateTime ?? ''
  const time = updateTime || createTime
  const attachments = normalizeNoticeAttachments(raw.attachments)
  const priority = normalizeNoticePriority(raw.priority)
  const scope = normalizeNoticeScope(raw.scope)
  const title = normalizeText(raw.notice_title ?? raw.title)
  const content = String(raw.notice_content ?? raw.content ?? '').trim()
  const publisher = normalizeText(raw.publisher)

  return {
    ...raw,
    id,
    notice_id: id,
    title,
    notice_title: title,
    content,
    notice_content: content,
    priority,
    priorityCode: getNoticePriorityCode(priority),
    priorityText: priority,
    department: normalizeText(raw.department),
    publisher,
    scope,
    attachments,
    attachmentCount: attachments.length,
    author: publisher,
    create_time: createTime,
    update_time: updateTime,
    rawTime: time,
    time: formatNoticeTime(time),
    displayTime: formatNoticeTime(time),
    readCount: toSafeNumber(raw.read_count ?? raw.readCount, 0)
  }
}

export function normalizeNoticeListPayload(payload, defaultPageSize = 10) {
  const rawList = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.list)
      ? payload.list
      : Array.isArray(payload)
        ? payload
        : []

  const list = rawList.map(normalizeNoticeItem).filter(Boolean)
  const rawPagination = payload?.pagination ?? {}
  const pageSize = toSafeNumber(
    rawPagination.page_size ?? rawPagination.pageSize,
    defaultPageSize
  ) || defaultPageSize
  const total = toSafeNumber(
    rawPagination.total ?? rawPagination.total_count ?? rawPagination.totalCount ?? payload?.count ?? payload?.total,
    list.length
  )
  const currentPage = toSafeNumber(
    rawPagination.page ?? rawPagination.current_page ?? rawPagination.currentPage,
    1
  )
  const totalPages = toSafeNumber(
    rawPagination.total_pages ?? rawPagination.totalPages,
    Math.max(1, Math.ceil(total / pageSize))
  )

  return {
    list,
    count: total,
    pagination: {
      page: currentPage,
      pageSize,
      total,
      totalPages
    }
  }
}

export function normalizeNoticeDetailPayload(payload) {
  return normalizeNoticeItem(payload?.data ?? payload)
}

export function extractNoticeTemplateVariables(...parts) {
  const seen = new Set()
  const variables = []

  parts
    .filter(Boolean)
    .forEach(text => {
      String(text).replace(/\{([^{}]+)\}/g, (_, key) => {
        const normalized = String(key).trim()
        if (normalized && !seen.has(normalized)) {
          seen.add(normalized)
          variables.push(normalized)
        }
        return _
      })
    })

  return variables
}

export function applyNoticeTemplateVariables(templateText, values = {}) {
  return String(templateText ?? '').replace(/\{([^{}]+)\}/g, (match, key) => {
    const normalized = String(key).trim()
    const nextValue = values[normalized]
    return nextValue == null || nextValue === '' ? match : String(nextValue)
  })
}

export function buildNoticeFromTemplate(template, values = {}) {
  const normalized = normalizeNoticeTemplateItem(template)
  return {
    title: applyNoticeTemplateVariables(normalized?.titleTemplate, values),
    content: applyNoticeTemplateVariables(normalized?.contentTemplate, values)
  }
}

export function normalizeNoticeTemplateTypeItem(raw) {
  if (!raw) return null
  return {
    key: normalizeText(raw.key),
    name: normalizeText(raw.name)
  }
}

export function normalizeNoticeTemplateTypesPayload(payload) {
  const rawList = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : []

  return rawList
    .map(normalizeNoticeTemplateTypeItem)
    .filter(item => item?.name)
}

export function normalizeNoticeTemplateItem(raw) {
  if (!raw) return null

  const id = raw.template_id ?? raw.id
  const titleTemplate = String(raw.title_template ?? raw.titleTemplate ?? '').trim()
  const contentTemplate = String(raw.content_template ?? raw.contentTemplate ?? '').trim()
  const priority = normalizeNoticePriority(raw.priority)

  return {
    ...raw,
    id,
    template_id: id,
    template_name: normalizeText(raw.template_name ?? raw.name),
    template_type: normalizeText(raw.template_type ?? raw.type),
    title_template: titleTemplate,
    titleTemplate,
    content_template: contentTemplate,
    contentTemplate,
    priority,
    priorityCode: getNoticePriorityCode(priority),
    department: normalizeText(raw.department),
    scope: normalizeNoticeScope(raw.scope),
    variables: extractNoticeTemplateVariables(titleTemplate, contentTemplate)
  }
}

export function normalizeNoticeTemplatesPayload(payload) {
  const rawList = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.list)
      ? payload.list
      : Array.isArray(payload)
        ? payload
        : []

  return {
    list: rawList.map(normalizeNoticeTemplateItem).filter(Boolean),
    count: toSafeNumber(payload?.count ?? rawList.length, rawList.length)
  }
}

export function normalizeNoticeClassItem(raw) {
  if (!raw) return null
  const id = raw.class_id ?? raw.id
  const name = normalizeText(raw.class_name ?? raw.name)
  const grade = normalizeText(raw.grade) || (name.match(/^(.+?)\(/)?.[1] ?? '')
  return {
    ...raw,
    id,
    class_id: id,
    class_name: name,
    name,
    grade
  }
}

export function normalizeNoticeClassesPayload(payload) {
  const rawList = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.list)
      ? payload.list
      : Array.isArray(payload)
        ? payload
        : []

  return rawList.map(normalizeNoticeClassItem).filter(Boolean)
}

export function getNoticesList(params = {}) {
  const url = buildUrl('/api/notices', {
    user_id: params.user_id,
    page: params.page ?? 1,
    page_size: params.page_size ?? 10
  })
  return request(url)
}

export function getTeacherNoticesList(params = {}) {
  const url = buildUrl('/api/notices/teacher', {
    user_id: params.user_id,
    page: params.page ?? 1,
    page_size: params.page_size ?? 10
  })
  return request(url)
}

export function getTeacherReceivedNotices(params = {}) {
  const url = buildUrl('/api/notices/teacher/received', {
    user_id: params.user_id,
    page: params.page ?? 1,
    page_size: params.page_size ?? 10
  })
  return request(url)
}

export function getNoticeDetail(noticeId, params = {}) {
  const url = buildUrl(`/api/notices/${noticeId}`, params)
  return request(url)
}

export function createNotice(data) {
  return request('/api/notices', {
    method: 'POST',
    body: data
  })
}

export function updateNotice(noticeId, data) {
  return request(`/api/notices/${noticeId}`, {
    method: 'PUT',
    body: data
  })
}

export function deleteNotice(noticeId) {
  return request(`/api/notices/${noticeId}`, {
    method: 'DELETE'
  })
}

export function sendNotice(noticeId, data) {
  return request(`/api/notices/${noticeId}/send`, {
    method: 'POST',
    body: data
  })
}

export function generateMedicalNoticeDraft(data) {
  return request('/api/school-medical-notifications/ai-generate', {
    method: 'POST',
    body: data
  })
}

export function getNoticeTemplateTypes() {
  return request('/api/notice-templates/types', { skipAuth: true })
}

export function getNoticeTemplates(params = {}) {
  const url = buildUrl('/api/notice-templates', {
    template_type: params.template_type
  })
  return request(url)
}

export function getNoticeTemplate(templateId) {
  return request(`/api/notice-templates/${templateId}`)
}

export function createNoticeTemplate(data) {
  return request('/api/notice-templates', {
    method: 'POST',
    body: data
  })
}

export function updateNoticeTemplate(templateId, data) {
  return request(`/api/notice-templates/${templateId}`, {
    method: 'PUT',
    body: data
  })
}

export function deleteNoticeTemplate(templateId) {
  return request(`/api/notice-templates/${templateId}`, {
    method: 'DELETE'
  })
}

export function getNoticeClasses(params = {}) {
  const url = buildUrl('/api/classes', {
    class_name: params.class_name ?? params.className,
    page: params.page ?? 1,
    page_size: params.page_size ?? params.pageSize ?? 500
  })
  return request(url)
}
